export const DEFAULT_SCENE_CAMERA_ID = 'DemoCamera';

export const VIEWPORT_CAMERA_NAMES = new Set([
  'perspectiveCamera',
  'orthographicCamera',
]);

/** Stable scene id for Redux ↔ Three.js name lookup. */
export function getCameraStableId(cameraRecord) {
  if (!cameraRecord) return null;
  return (
    cameraRecord._id ||
    cameraRecord.id ||
    cameraRecord.temp_file_name ||
    DEFAULT_SCENE_CAMERA_ID
  );
}

export function getCameraHelperVisible(cameraRecord) {
  return (
    cameraRecord?.helperVisible === true ||
    cameraRecord?.camera_helper_visible === true
  );
}

/** Normalize API / Redux camera records so scene name and Redux id always match. */
export function normalizeCameraFromApi(cam) {
  if (!cam) return cam;
  const stableId = getCameraStableId(cam);
  const label = cam.name || cam.file_name || 'Camera';
  return {
    ...cam,
    _id: stableId,
    id: stableId,
    temp_file_name: cam.temp_file_name || stableId,
    name: label,
    file_name: cam.file_name || label,
    helperVisible: getCameraHelperVisible(cam),
  };
}

/**
 * Helper is shown when helperVisible is true,
 * or temporarily while the camera is selected (even if helperVisible is false).
 * Same pattern as light helpers via shouldShowLightHelper.
 */
export function shouldShowCameraHelper(
  cameraRecord,
  { selectedCameraId } = {}
) {
  if (!cameraRecord) return false;

  const stableId = getCameraStableId(cameraRecord);
  if (
    selectedCameraId &&
    (stableId === selectedCameraId ||
      cameraRecord._id === selectedCameraId ||
      cameraRecord.id === selectedCameraId ||
      cameraRecord.temp_file_name === selectedCameraId ||
      cameraRecord.name === selectedCameraId)
  ) {
    return true;
  }

  return getCameraHelperVisible(cameraRecord);
}

/** Prefer selected camera when it should show a helper; else first helperVisible camera. */
export function resolveCameraHelperTarget(
  scene,
  cameraProperties = [],
  { selectedCameraId, hideAll = false } = {}
) {
  if (!scene || hideAll) return null;

  if (selectedCameraId) {
    const selectedProp = cameraProperties.find(
      (cam) =>
        cam._id === selectedCameraId ||
        cam.id === selectedCameraId ||
        cam.temp_file_name === selectedCameraId
    );
    const selectedCamera =
      scene.getObjectByName(selectedCameraId) ||
      resolveSceneCameraObject(scene, selectedProp || { _id: selectedCameraId });

    if (
      isSceneCamera(selectedCamera) &&
      shouldShowCameraHelper(selectedProp || { _id: selectedCameraId }, {
        selectedCameraId,
      })
    ) {
      return selectedCamera;
    }
  }

  for (const camProp of cameraProperties) {
    if (!getCameraHelperVisible(camProp)) continue;
    const sceneCamera = resolveSceneCameraObject(scene, camProp);
    if (isSceneCamera(sceneCamera)) {
      return sceneCamera;
    }
  }

  return null;
}

/**
 * Sync all camera helpers independently (like light helpers).
 * Each camera with helperVisible=true keeps its helper visible in design;
 * selected camera also shows a helper while selected.
 */
export function syncCameraHelperVisibility(
  scene,
  cameraProperties,
  showCameraHelper,
  { selectedCameraId, hideAll = false } = {}
) {
  if (!scene || typeof showCameraHelper !== 'function') return;

  if (hideAll) {
    showCameraHelper(false);
    return;
  }

  const props = Array.isArray(cameraProperties) ? cameraProperties : [];
  const handledIds = new Set();

  props.forEach((camProp) => {
    const sceneCamera = resolveSceneCameraObject(scene, camProp);
    if (!isSceneCamera(sceneCamera)) return;

    const cameraId = sceneCamera.name || getCameraStableId(camProp);
    if (!cameraId || handledIds.has(cameraId)) return;
    handledIds.add(cameraId);

    const show = shouldShowCameraHelper(camProp, { selectedCameraId });
    if (show) {
      showCameraHelper(true, sceneCamera, cameraId);
    } else {
      showCameraHelper(false, sceneCamera, cameraId);
    }
  });

  // Hide leftover helpers for scene cameras not covered by Redux props.
  scene.children.forEach((child) => {
    if (!(child?.type === 'CameraHelper' || child?.isCameraHelper)) return;
    const cameraId = child.name?.replace(/Helper$/, '');
    if (!cameraId || handledIds.has(cameraId)) return;
    const sceneCamera = scene.getObjectByName(cameraId);
    if (isSceneCamera(sceneCamera)) {
      const show =
        selectedCameraId &&
        (sceneCamera.name === selectedCameraId || cameraId === selectedCameraId);
      if (show) {
        showCameraHelper(true, sceneCamera, cameraId);
      } else {
        showCameraHelper(false, sceneCamera, cameraId);
      }
    }
  });
}

export function isViewportCamera(obj) {
  return !!(obj && VIEWPORT_CAMERA_NAMES.has(obj.name));
}

export function isSceneCamera(obj) {
  return (
    obj &&
    (obj.isPerspectiveCamera || obj.isOrthographicCamera) &&
    !isViewportCamera(obj)
  );
}

export function isSceneCameraPoint(obj) {
  if (!obj?.name || !obj.name.endsWith('Point')) return false;
  const cameraId = obj.name.slice(0, -'Point'.length);
  return cameraId.length > 0 && !VIEWPORT_CAMERA_NAMES.has(cameraId);
}

export function resolveSceneCameraFromPoint(scene, pointObj) {
  if (!scene || !pointObj?.name?.endsWith('Point')) return null;
  const cameraId = pointObj.name.slice(0, -'Point'.length);
  const camera = scene.getObjectByName(cameraId);
  return isSceneCamera(camera) ? camera : null;
}

export function isDescendantOfSceneCamera(obj) {
  if (!obj) return false;
  let current = obj.parent;
  while (current) {
    if (isSceneCamera(current)) return true;
    current = current.parent;
  }
  return false;
}

export function isSceneCameraOrPoint(obj) {
  return isSceneCamera(obj) || isSceneCameraPoint(obj);
}

export function resolveSceneCameraObject(scene, item) {
  if (!scene || !item) return null;

  const ids = [
    item._id,
    item.id,
    item.temp_file_name,
    item.name !== 'Camera' ? item.name : null,
    DEFAULT_SCENE_CAMERA_ID,
  ].filter(Boolean);

  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (isSceneCamera(obj)) return obj;
  }

  let found = null;
  scene.traverse((child) => {
    if (!found && isSceneCamera(child)) {
      found = child;
    }
  });
  return found;
}
